import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { getUrlPrefix, Language } from '../utils/languageUtils';

/**
 * SEO Component - Manages meta tags, canonical URLs, and hreflang attributes
 *
 * This component directly manipulates the DOM to inject SEO tags into the <head>.
 * This approach is used because react-helmet-async has compatibility issues with
 * React 19. The tags are managed through useEffect to ensure proper cleanup.
 *
 * IMPORTANT: In development mode (and client-side only builds), the SEO tags
 * are injected into the DOM via JavaScript after React mounts. This means:
 *
 * 1. "View Source" will NOT show these tags (it shows the static HTML template)
 * 2. Browser DevTools (Elements tab) WILL show these tags (it shows the live DOM)
 * 3. For search engines to see these tags without JS execution, consider:
 *    - Pre-rendering (e.g., react-snap)
 *    - Server-side rendering (SSR)
 *    - Dynamic rendering service
 *
 * To verify SEO tags in dev mode: Open DevTools > Elements tab > expand <head>
 */
interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  noIndex?: boolean;
}

const DOMAIN = 'https://fireandice.ee';
const SUPPORTED_URL_PREFIXES = ['en', 'ee', 'fi', 'ru'] as const;
const INTERNAL_LANGUAGES: Language[] = ['en', 'et', 'fi', 'ru'];

/**
 * Maps internal language code to ISO hreflang code
 */
const getHreflangCode = (lang: Language): string => {
  switch (lang) {
    case 'et': return 'et-EE';
    case 'en': return 'en-US';
    case 'fi': return 'fi-FI';
    case 'ru': return 'ru-RU';
    default: return 'en-US';
  }
};

// Helper to create or update a meta tag
const setMetaTag = (attribute: string, value: string, content: string): HTMLMetaElement => {
  let element = document.querySelector(`meta[${attribute}="${value}"]`) as HTMLMetaElement;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
  element.setAttribute('data-seo', 'true');
  return element;
};

// Helper to create or update a link tag
const setLinkTag = (rel: string, href: string, hreflang?: string): HTMLLinkElement => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let element = document.querySelector(selector) as HTMLLinkElement;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    if (hreflang) {
      element.setAttribute('hreflang', hreflang);
    }
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
  element.setAttribute('data-seo', 'true');
  return element;
};

// Helper to remove all SEO-managed tags
const cleanupSeoTags = () => {
  document.querySelectorAll('[data-seo="true"]').forEach(el => el.remove());
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  noIndex = false
}) => {
  const { language, t } = useI18n();
  const location = useLocation();
  const previousTitle = useRef<string | null>(null);

  // Construct canonical URL
  // URL structure is /:lang/:rest where lang is en/ee/fi/ru
  const currentPath = location.pathname;
  const pathParts = currentPath.split('/').filter(Boolean);

  // Identify if the first part is a URL language prefix
  const currentUrlLangPrefix = SUPPORTED_URL_PREFIXES.find(l => l === pathParts[0]);

  // Get the "slug" part (everything after language prefix)
  const slugParts = currentUrlLangPrefix
    ? pathParts.slice(1)
    : pathParts;
  const cleanSlug = slugParts.join('/');

  // Build canonical URL using current language's URL prefix
  const currentLangUrlPrefix = getUrlPrefix(language);
  const canonicalPath = cleanSlug ? `/${currentLangUrlPrefix}/${cleanSlug}` : `/${currentLangUrlPrefix}`;
  const canonicalUrl = `${DOMAIN}${canonicalPath}`;

  // Default SEO content
  const defaultTitle = t.seo?.defaultTitle || "Fire & Ice - Wellness & Recovery";
  const defaultDesc = t.seo?.defaultDescription || "Experience the ultimate wellness journey with our premium services: Sauna, Cold Plunge, and more.";
  const finalTitle = title ? `${title} | Fire & Ice` : defaultTitle;
  const finalDescription = description || defaultDesc;

  useEffect(() => {
    // Store original title for cleanup
    if (previousTitle.current === null) {
      previousTitle.current = document.title;
    }

    // Only set document title if a title prop was explicitly provided
    // Pages use useDocumentTitle hook to set their own titles
    if (title) {
      document.title = finalTitle;
    }

    // Set meta description
    setMetaTag('name', 'description', finalDescription);

    // Set Open Graph tags
    setMetaTag('property', 'og:title', finalTitle);
    setMetaTag('property', 'og:description', finalDescription);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:type', 'website');

    if (image) {
      setMetaTag('property', 'og:image', image);
    }

    // Set robots tag if noIndex
    if (noIndex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    }

    // Set canonical link
    setLinkTag('canonical', canonicalUrl);

    // Set hreflang tags for all supported languages
    INTERNAL_LANGUAGES.forEach((lang) => {
      const urlPrefix = getUrlPrefix(lang);
      const hreflangPath = cleanSlug ? `/${urlPrefix}/${cleanSlug}` : `/${urlPrefix}`;
      setLinkTag('alternate', `${DOMAIN}${hreflangPath}`, getHreflangCode(lang));
    });

    // Set x-default hreflang (points to English version as the default fallback)
    setLinkTag('alternate', `${DOMAIN}/en${cleanSlug ? `/${cleanSlug}` : ''}`, 'x-default');

    // Cleanup function
    return () => {
      cleanupSeoTags();
    };
  }, [title, finalTitle, finalDescription, canonicalUrl, image, noIndex, cleanSlug]);

  // This component doesn't render anything visible
  return null;
};

export default SEO;
